import { Component } from '@angular/core';
import { AbstractUnits } from '../abstract-units';
import { ProjectDetailsTab } from 'src/app/project-details/ProjectDetailsTab';
import { GridConnection } from 'src/app/shared/model/technical/GridConnection';
import { UnitTypeEntry } from '../UnitTypeEntry';
import { ReadOnlyReason, ReadOnlyReasonToTooltip } from 'src/app/project-details/ReadOnlyStateManager';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { GridConnectionType, gridConnectionTypeToLabel } from '../../../model/technical/GridConnectionType';
import { GridConnectionMethod, gridConnectionMethodToLabel } from '../../../model/technical/GridConnectionMethod';
import {
  GridConnectionReadiness,
  gridConnectionReadinessToLabel,
} from '../../../model/technical/GridConnectionReadiness';
import {
  GridConnectionMethodType,
  gridConnectionMethodTypeToLabel,
} from '../../../model/technical/GridConnectionMethodType';
import { DateUtil } from '../../../DateUtil';
import { ValidatorUtil } from 'src/app/shared/ValidatorUtil';

@Component({
  selector: 'digires-grid-connections',
  templateUrl: './grid-connections.component.html',
  styleUrls: ['./grid-connections.component.scss'],
})
export class GridConnectionsComponent extends AbstractUnits {
  public static createDefaultUnits(
    gridA?: GridConnection,
    gridB?: GridConnection,
    gridC?: GridConnection,
    gridD?: GridConnection,
  ): UnitTypeEntry[] {
    return GridConnectionsComponent.createDefaultUnitsInternal(
      gridA || GridConnectionsComponent.createDefaultUnit('gridA', 'A'),
      gridB || GridConnectionsComponent.createDefaultUnit('gridB', 'B'),
      gridC || GridConnectionsComponent.createDefaultUnit('gridC', 'C'),
      gridD || GridConnectionsComponent.createDefaultUnit('gridD', 'D'),
    );
  }

  private static createDefaultUnitsInternal(
    gridA: GridConnection,
    gridB: GridConnection,
    gridC: GridConnection,
    gridD: GridConnection,
  ): UnitTypeEntry[] {
    return [
      {
        id: 'gridConnectionA',
        active: true,
        canAdd: false,
        canDelete: false,
        label: gridA.gridFieldsTypeLabel,
        fg: GridConnectionsComponent.createGridFg(gridA),
      },
      {
        id: 'gridConnectionB',
        active: gridB.active,
        canAdd: false,
        canDelete: false,
        label: gridB.gridFieldsTypeLabel,
        fg: GridConnectionsComponent.createGridFg(gridB),
      },
      {
        id: 'gridConnectionC',
        active: gridC.active,
        canAdd: false,
        canDelete: false,
        label: gridC.gridFieldsTypeLabel,
        fg: GridConnectionsComponent.createGridFg(gridC),
      },
      {
        id: 'gridConnectionD',
        active: gridD.active,
        canAdd: false,
        canDelete: false,
        label: gridD.gridFieldsTypeLabel,
        fg: GridConnectionsComponent.createGridFg(gridD),
      },
    ];
  }

  private static createGridFg(unit: GridConnection): FormGroup {
    const transformers = unit.transformerEntries || [];
    if (transformers.length < 1) {
      transformers.push({ id: null, manufacturer: '', mvaRating: '' });
    }
    const transformerEntryFormGroups = transformers.map((entry) => {
      return new FormGroup({
        id: new FormControl(entry.id),
        manufacturer: new FormControl(entry.manufacturer),
        mvaRating: new FormControl(entry.mvaRating),
      });
    });

    const meters = unit.meterEntries || [];
    if (meters.length < 1) {
      meters.push({ id: null, ref: '', type: null });
    }
    const meterEntryFormGroups = meters.map((entry) => {
      return new FormGroup({
        id: new FormControl(entry.id),
        ref: new FormControl(entry.ref),
        type: new FormControl(entry.type),
      });
    });

    const fg = new FormGroup({
      // Types
      gridFieldsTypeLabel: new FormControl(unit.gridFieldsTypeLabel),
      // Grid Operator
      networkOperator: new FormControl(unit.networkOperator),
      gridOperatorPowerPlantId: new FormControl(unit.gridOperatorPowerPlantId),
      networkOperatorContact: new FormControl(unit.networkOperatorContact),
      nameOfTSO: new FormControl(unit.nameOfTSO),
      nameOfDirectMarketer: new FormControl(unit.nameOfDirectMarketer),
      // Grid Connection
      connectionType: new FormControl(unit.connectionType),
      connectionAgreementCapacity: new FormControl(unit.connectionAgreementCapacity, [Validators.min(0)]),
      connectionVoltage: new FormControl(unit.connectionVoltage),
      connectionPointLatitude: new FormControl(unit.connectionPointLatitude),
      connectionPointLongitude: new FormControl(unit.connectionPointLongitude),
      connectionPoint: new FormControl(unit.connectionPoint),
      connectionReadinessExternal: new FormControl(unit.connectionReadinessExternal),
      mainTransformerManufacturer: new FormControl(unit.mainTransformerManufacturer),
      transformerMvaRatingExternal: new FormControl(unit.transformerMvaRatingExternal),
      typeOfConnectionMethod: new FormControl(unit.typeOfConnectionMethod),
      typeOfConnectionNotes: new FormControl(unit.typeOfConnectionNotes),
      connectionDistance: new FormControl(unit.connectionDistance),
      connectionMethod: new FormControl(unit.connectionMethod),
      // On Site Satellite Substations
      numberOfSubstations: new FormControl(unit.numberOfSubstations, [Validators.min(0)]),
      satelliteSubstationManufacturer: new FormControl(unit.satelliteSubstationManufacturer),
      // Main On Site Substation
      externalSubstationOperator: new FormControl(unit.externalSubstationOperator),
      transformerEntries: new FormArray(transformerEntryFormGroups),
      substationNotes: new FormControl(unit.substationNotes),
      gridOperatorSubstationEarthingMethod: new FormControl(unit.gridOperatorSubstationEarthingMethod),
      // Metering
      meterEntries: new FormArray(meterEntryFormGroups),
      meteringNotes: new FormControl(unit.meteringNotes),
      // On Site Collection Network
      internalVoltage: new FormControl(unit.internalVoltage, [Validators.min(0)]),
      numberOfTransformers: new FormControl(unit.numberOfTransformers, [Validators.min(0)]),
      manufacturerOfTransformers: new FormControl(unit.manufacturerOfTransformers),
      mvaRatingMvTransformers: new FormControl(unit.mvaRatingMvTransformers),
      numberOfFeederCircuits: new FormControl(unit.numberOfFeederCircuits, [Validators.min(0)]),
      feederCircuitIds: new FormControl(unit.feederCircuitIds),
      namesOfSiteOperators: new FormControl(unit.namesOfSiteOperators),
      // General
      comment: new FormControl(unit.comment),
      // Important Dates
      screeningDate: new FormControl(DateUtil.formField(unit.screeningDate), [ValidatorUtil.validateDate]),
      assessmentDate: new FormControl(DateUtil.formField(unit.assessmentDate), [ValidatorUtil.validateDate]),
      connectionOfferSubmittedDate: new FormControl(DateUtil.formField(unit.connectionOfferSubmittedDate), [
        ValidatorUtil.validateDate,
      ]),
      connectionOfferReceivedDate: new FormControl(DateUtil.formField(unit.connectionOfferReceivedDate), [
        ValidatorUtil.validateDate,
      ]),
      connectionOfferSignOffDate: new FormControl(DateUtil.formField(unit.connectionOfferSignOffDate), [
        ValidatorUtil.validateDate,
      ]),
      connectionOfferAcceptedDate: new FormControl(DateUtil.formField(unit.connectionOfferAcceptedDate), [
        ValidatorUtil.validateDate,
      ]),
      energisationDateAgreed: new FormControl(DateUtil.formField(unit.energisationDateAgreed), [
        ValidatorUtil.validateDate,
      ]),
      energisationDateActual: new FormControl(DateUtil.formField(unit.energisationDateActual), [
        ValidatorUtil.validateDate,
      ]),
      energisationDateDifference: new FormControl(),
    });
    GridConnectionsComponent.calculateEnergisationDateDifference(fg);
    return fg;
  }

  public static calculateEnergisationDateDifference(fg: FormGroup) {
    const fgValue = fg.value;

    const agreed = DateUtil.anyToDate(fgValue.energisationDateAgreed);
    const actual = DateUtil.anyToDate(fgValue.energisationDateActual);

    fg.patchValue({
      energisationDateDifference: GridConnectionsComponent.getEnergisationDateDiffValue(agreed, actual),
    });
  }

  public static getEnergisationDateDiffValue(agreed: Date, actual: Date): string {
    if (!DateUtil.isDate(agreed) || !DateUtil.isDate(actual)) {
      return '';
    }
    const daysDiff = DateUtil.daysBetween(agreed, actual);
    if (daysDiff === 0) {
      return 'On Time';
    }
    if (daysDiff < 0) {
      return `${-daysDiff} days after agreement`;
    }
    return `${daysDiff} days before agreement`;
  }

  private static createDefaultUnit(id: string, suffix: string): GridConnection {
    return {
      id: id,
      active: suffix === 'A',
      // Types
      gridFieldsTypeLabel: `Connection Type ${suffix}`,
      // Grid Operator
      networkOperator: null,
      gridOperatorPowerPlantId: null,
      networkOperatorContact: null,
      nameOfTSO: null,
      nameOfDirectMarketer: null,
      // Grid Connection
      connectionType: null,
      connectionAgreementCapacity: null,
      connectionVoltage: null,
      connectionPointLatitude: null,
      connectionPointLongitude: null,
      connectionPoint: null,
      connectionReadinessExternal: null,
      mainTransformerManufacturer: null,
      transformerMvaRatingExternal: null,
      typeOfConnectionMethod: null,
      typeOfConnectionNotes: null,
      connectionDistance: null,
      connectionMethod: null,
      // On Site Satellite Substations
      numberOfSubstations: null,
      satelliteSubstationManufacturer: null,
      // Main On Site Substation
      externalSubstationOperator: null,
      transformerEntries: [],
      substationNotes: null,
      gridOperatorSubstationEarthingMethod: null,
      // Metering
      meterEntries: [],
      meteringNotes: null,
      // On Site Collection Network
      internalVoltage: null,
      numberOfTransformers: null,
      manufacturerOfTransformers: null,
      mvaRatingMvTransformers: null,
      numberOfFeederCircuits: null,
      feederCircuitIds: null,
      namesOfSiteOperators: 'RWE',
      // General
      comment: null,
      // Important Dates
      screeningDate: null,
      assessmentDate: null,
      connectionOfferSubmittedDate: null,
      connectionOfferReceivedDate: null,
      connectionOfferSignOffDate: null,
      connectionOfferAcceptedDate: null,
      energisationDateAgreed: null,
      energisationDateActual: null,
    };
  }

  readonly readOnlyReasonCalculated = ReadOnlyReason.CALCULATED;
  readonly calculatedReadOnlyReason = ReadOnlyReasonToTooltip[ReadOnlyReason.CALCULATED];
  readonly allConnectionTypes = Object.values(GridConnectionType);
  readonly allTypesOfConnectionMethods = Object.values(GridConnectionMethodType);
  readonly allConnectionMethods = Object.values(GridConnectionMethod);
  readonly allConnectionReadinessOptions = Object.values(GridConnectionReadiness);

  constructor() {
    super(ProjectDetailsTab.grid, 0 /*unused*/);
  }

  connectionTypeToLabel(value: GridConnectionType): string {
    return gridConnectionTypeToLabel[value];
  }

  connectionMethodTypeToLabel(value: GridConnectionMethodType): string {
    return gridConnectionMethodTypeToLabel[value];
  }

  connectionMethodToLabel(value: GridConnectionMethod): string {
    return gridConnectionMethodToLabel[value];
  }

  connectionReadinessOptionToLabel(value: GridConnectionReadiness): string {
    return gridConnectionReadinessToLabel[value];
  }
}
